import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import unitService, { type Unit } from "../services/unit";

type UnitSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useUnit() {
  const { t } = useI18n();
  const units = ref<Unit[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedUnit = ref<Unit | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Unit>({
    code: "",
    name: "",
    short_name: "",
    description: "",
    status: 1,
  });

  const fetchUnits = async () => {
    loading.value = true;
    try {
      units.value = await unitService.getUnits();
    } catch (error) {
      console.error("Failed to fetch units:", error);
      Swal.fire({
        icon: "error",
        title: t("unit_page.load_failed"),
        text: t("unit_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredUnits = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return units.value;

    return units.value.filter((unit) =>
      [unit.code, unit.name, unit.short_name, unit.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("unit_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("unit_page.items_per_page") },
  }));

  watch(
    filteredUnits,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      short_name: "",
      description: "",
      status: 1,
    };
    errors.value = {};
  };

  const openCreateModal = () => {
    selectedUnit.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (unit: Unit) => {
    selectedUnit.value = unit;
    errors.value = {};
    form.value = {
      id: unit.id,
      code: unit.code || "",
      name: unit.name || "",
      short_name: unit.short_name || "",
      description: unit.description || "",
      status: Number(unit.status ?? 1),
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = (field: keyof Unit, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const generateUnitCode = async () => {
    try {
      if (!units.value.length) {
        await fetchUnits();
      }

      const nextNumber =
        units.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `UN${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate unit code:", error);
      Swal.fire({
        icon: "error",
        title: t("unit_page.generate_failed"),
        text: t("unit_page.generate_failed_text"),
      });
    }
  };

  const saveUnit = async () => {
    loading.value = true;
    errors.value = {};
    try {
      const payload = {
        code: form.value.code || "",
        name: form.value.name || "",
        short_name: form.value.short_name || undefined,
        description: form.value.description || undefined,
        status: Number(form.value.status ?? 1),
      };

      if (selectedUnit.value?.id) {
        await unitService.updateUnit(selectedUnit.value.id, payload);
      } else {
        await unitService.createUnit(payload);
      }

      await fetchUnits();
      await Swal.fire({
        icon: "success",
        title: selectedUnit.value ? t("unit_page.updated_title") : t("unit_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as UnitSaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const message =
          requestError.response?.data?.message ||
          Object.values(requestError.response?.data?.errors || {}).flat()[0] ||
          t("unit_page.save_failed_text");
        Swal.fire({
          icon: "error",
          title: t("unit_page.save_failed"),
          text: message as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteUnit = async (unit: Unit) => {
    if (!unit?.id) return;

    const result = await Swal.fire({
      title: t("unit_page.delete_title"),
      text: t("unit_page.delete_text", { name: unit.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("unit_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("unit_page.deleting_title"),
        text: t("unit_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await unitService.deleteUnit(unit.id);
      await fetchUnits();

      Swal.fire({
        icon: "success",
        title: t("unit_page.deleted_title"),
        text: t("unit_page.deleted_text", { name: unit.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("unit_page.delete_failed"),
        text: t("unit_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredUnits,
    showModal,
    selectedUnit,
    form,
    errors,
    loading,
    paginationConfig,
    fetchUnits,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    generateUnitCode,
    saveUnit,
    deleteUnit,
  };
}
